import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
// Create a shared atom for the MCP wizard state
import { atom, useAtom } from 'jotai'

import type { GetMcpServersData } from '~/generated/client'
import { useUser } from '~/dataProcessor/hooks/user'
import { useToast } from '~/design-system/atom/use-toast'
import {
  deleteMcpById,
  getMcpById,
  getMcpServers,
  postMcpServers,
} from '~/generated/client'

type McpClient = 'cursor' | 'claude' | 'windsurf'

// CODE_SAMPLES reference
export const CODE_SAMPLES = {
  START_MCP_CLIENT: {
    python: (
      connectedAccountId: string,
      actions: string[],
      apiKey: string,
    ) => `from composio import ComposioToolSet, App
toolset = ComposioToolSet(api_key="${apiKey}")

mcp_Server = toolset.get_mcp_server(connected_account_id="${connectedAccountId}"])
return mcp_server.get_signed_url()`,
    javascript: (
      connectedAccountId: string,
      actions: string[],
      apiKey: string,
    ) => `import { OpenAIToolSet } from 'composio-core';
const toolset = new OpenAIToolSet({ apiKey: "${apiKey}" });

const mcpServer = toolset.getMcpServer(connectedAccountId="${connectedAccountId}")
return mcpServer.getSignedUrl();`,
  },
}
export const useMcpServers = (params: GetMcpServersData) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['mcp-servers'],
    queryFn: () => getMcpServers(params),
    staleTime: 0,
    cacheTime: 0,
  })

  return {
    data: data?.items || [],
    isLoading,
    isError,
  }
}

export const useMcpServerById = (id: string) => {
  return useQuery({
    queryKey: ['mcp-server', id],
    queryFn: () => getMcpById({ id }),
    enabled: !!id,
  })
}

export const useDeleteMcpServer = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: deleteMcpById,
    onSuccess: () => {
      toast({
        title: 'Server deleted',
        description: 'The MCP server has been successfully deleted.',
        variant: 'success',
      })
      queryClient.invalidateQueries({ queryKey: ['mcp-servers'] })
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to delete the MCP server. Please try again.',
        variant: 'destructive',
      })
    },
  })
}

export const useCreateMcpServer = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: postMcpServers,
    onSuccess: () => {
      toast({
        title: 'Server created',
        description: 'The MCP server has been successfully created.',
        variant: 'success',
      })
      queryClient.invalidateQueries({ queryKey: ['mcp-servers'] })
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to create the MCP server. Please try again.',
        variant: 'destructive',
      })
    },
  })
}

// This function calls the API to get the server endpoint
export const getMcpServerEndpoint = async (
  id: string,
  client: string,
): Promise<string> => {
  try {
    const response = await axios.post(
      `${process.env.NEXT_PUBLIC_API_URL}/api/v1/mcp/servers/${id}/generate_command`,
      { mcp_client: client },
      { withCredentials: true },
    )

    return response.data.url || response.data.command
  } catch (error) {
    throw new Error('Failed to get MCP server endpoint')
  }
}

interface UseNewServerWizardProps {
  appName: string
  initialServerId?: string
  serverURL?: string
  connectedAccountId?: string
  initialEntityId?: string
  onStepChange?: (step: number) => void
  connectorInfoId?: string
}

// Define the type for our MCP wizard state
interface McpWizardState {
  serverName: string
  limitedActions: string[]
  currentStep: number
  selectedTab: string
  selectedCodeLanguage: string
  serverEndpoint: string
  serverId?: string
  connectedId?: string
  entityId?: string
  isLoading: boolean
  isWizardOpen: boolean
  serverURL: string | null
}

const getMCPEndpoint = (
  mcpURL: URL,
  params: { entity_id?: string | undefined; client: McpClient },
) => {
  if (params.entity_id) {
    mcpURL.searchParams.append('user_id', params.entity_id)
  }

  switch (params.client) {
    case 'cursor':
      mcpURL.searchParams.append('agent', 'cursor')
      return `npx @composio/mcp@latest setup "${mcpURL.toString()}" --client cursor`
    case 'claude':
      mcpURL.searchParams.append('agent', 'claude')
      return `npx @composio/mcp@latest setup "${mcpURL.toString()}" --client claude`
    case 'windsurf':
      mcpURL.searchParams.append('agent', 'windsurf')
      return `npx @composio/mcp@latest setup "${mcpURL.toString()}" --client windsurf`
  }
}

// Create the atom with default values
const mcpWizardAtom = atom<McpWizardState>({
  serverName: '',
  limitedActions: [],
  currentStep: 1,
  selectedTab: 'cursor',
  selectedCodeLanguage: 'python',
  serverEndpoint: '',
  serverId: undefined,
  connectedId: undefined,
  entityId: undefined,
  isLoading: false,
  isWizardOpen: false,
  serverURL: null,
})

export const useNewServerWizard = ({
  appName,
  initialServerId,
  serverURL,
  connectedAccountId,
  initialEntityId,
  onStepChange,
  connectorInfoId,
}: UseNewServerWizardProps) => {
  const [wizardState, setWizardState] = useAtom(mcpWizardAtom)
  const { toast } = useToast()
  const { user } = useUser()

  const apiKey = user?.api_key?.key ?? undefined

  // Setters for individual state properties
  const setServerName = (value: string) =>
    setWizardState((prev) => ({ ...prev, serverName: value }))

  const setLimitedActions = (value: string[]) =>
    setWizardState((prev) => ({ ...prev, limitedActions: value }))

  const setCurrentStep = (value: number) => {
    setWizardState((prev) => ({ ...prev, currentStep: value }))
    if (onStepChange) onStepChange(value)
  }

  const setSelectedTab = (value: string) =>
    setWizardState((prev) => ({ ...prev, selectedTab: value }))

  const setSelectedCodeLanguage = (value: string) =>
    setWizardState((prev) => ({ ...prev, selectedCodeLanguage: value }))

  const setServerURL = (value: string) =>
    setWizardState((prev) => ({ ...prev, serverURL: value }))

  const setServerEndpoint = (value: string) =>
    setWizardState((prev) => ({ ...prev, serverEndpoint: value }))

  const setServerId = (value?: string) =>
    setWizardState((prev) => ({ ...prev, serverId: value }))

  const setConnectedId = (value?: string) =>
    setWizardState((prev) => ({ ...prev, connectedId: value }))

  const setEntityId = (value?: string) =>
    setWizardState((prev) => ({ ...prev, entityId: value }))

  const setIsLoading = (value: boolean) =>
    setWizardState((prev) => ({ ...prev, isLoading: value }))

  const setIsWizardOpen = (value: boolean) =>
    setWizardState((prev) => ({ ...prev, isWizardOpen: value }))

  const resetState = () => {
    setWizardState({
      serverName: '',
      limitedActions: [],
      currentStep: 1,
      selectedTab: 'cursor',
      selectedCodeLanguage: 'python',
      serverEndpoint: '',
      serverId: initialServerId,
      connectedId: connectedAccountId,
      entityId: initialEntityId,
      isLoading: false,
      isWizardOpen: false,
      serverURL: serverURL ?? null,
    })
  }

  const handleClose = () => {
    resetState()
    setIsWizardOpen(false)
  }
  const createMcpServerMutation = useCreateMcpServer()

  const handleSubmit = async () => {
    if (!wizardState.serverName) {
      toast({
        title: 'Missing information',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      })
      return
    }

    setIsLoading(true)
    try {
      const response = await createMcpServerMutation.mutateAsync({
        requestBody: {
          name: wizardState.serverName,
          allowed_tools: wizardState.limitedActions,
          auth_config_ids: [connectorInfoId as string],
        },
      })

      if (response) {
        setServerURL(response.mcp_url)
        toast({
          title: 'Server created',
          description: 'New MCP server has been successfully created',
          variant: 'success',
        })
        if (wizardState.currentStep === 1) {
          setServerId(response.id)
          const nextStep = 2
          setCurrentStep(nextStep)
        } else {
          handleClose()
        }
      } else {
        throw new Error('Failed to create MCP server')
      }
    } catch(error) {
      // @ts-expect-error no type for error
      const errorMessage = error.data.error.message
      toast({
        title: 'Error',
        description: errorMessage ? errorMessage : 'Failed to create MCP server',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const generateCommand = async () => {
    if (!wizardState.serverId) return

    try {
      const mcpURL = new URL(wizardState.serverURL as string)
      const command = getMCPEndpoint(mcpURL, {
        entity_id: wizardState.entityId,
        client: wizardState.selectedTab as McpClient,
      })
      setServerEndpoint(command)
    } catch (error) {
      console.error(error)
      toast({
        title: 'Error',
        description: 'Failed to generate server endpoint',
        variant: 'destructive',
      })
    }
  }

  // Update server id when initialServerId changes
  useEffect(() => {
    if (initialServerId !== wizardState.serverId) {
      setServerId(initialServerId)
    }
  }, [initialServerId])

  useEffect(() => {
    if (serverURL !== wizardState.serverURL && serverURL) {
      setServerURL(serverURL)
    }
  }, [serverURL])

  // Initialize connected account and user IDs
  useEffect(() => {
    if (connectedAccountId !== wizardState.connectedId) {
      setConnectedId(connectedAccountId)
    }
    if (initialEntityId !== wizardState.entityId) {
      setEntityId(initialEntityId)
    }
  }, [connectedAccountId, initialEntityId])

  // Fetch the server endpoint when step or tab changes
  useEffect(() => {
    if (wizardState.serverId && wizardState.currentStep === 2) {
      generateCommand()
    }
  }, [wizardState.serverId, wizardState.currentStep, wizardState.selectedTab])

  return {
    // State
    serverName: wizardState.serverName,
    setServerName,
    limitedActions: wizardState.limitedActions,
    setLimitedActions,
    currentStep: wizardState.currentStep,
    setCurrentStep,
    selectedTab: wizardState.selectedTab,
    setSelectedTab,
    selectedCodeLanguage: wizardState.selectedCodeLanguage,
    setSelectedCodeLanguage,
    serverEndpoint: wizardState.serverEndpoint,
    serverId: wizardState.serverId,
    connectedId: wizardState.connectedId,
    setConnectedId,
    entityId: wizardState.entityId,
    setEntityId,
    isLoading: wizardState.isLoading,
    apiKey,
    isWizardOpen: wizardState.isWizardOpen,
    setIsWizardOpen,
    // Actions
    handleSubmit,
    generateCommand,
    handleClose,
    resetState,
    serverURL: wizardState.serverURL,
    setServerURL,

    // Code samples
    CODE_SAMPLES,
  }
}
